// Детайлът по дни на един сервитьор.
//
// Заварената справка отваряше същите данни като списък от карти. Тук те са
// същата таблица като на екрана отзад — със сортиране, ред „Общо" и Excel, —
// защото въпросът „в кой ден е паднал оборотът" се чете по колона, не по карти.
//
// Esc и кликът на фона се обработват от самия antd (`onCancel`); собствен
// window listener НЕ се закача — той би затворил модала И би пуснал
// глобалната навигация „назад" със същото натискане.

import { useCallback, useState } from "react";
import { Modal } from "antd";
import { CalendarOutlined, CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { useViewport } from "../../../../hooks/useViewport";
import { modalStyles, modalWrapStyle } from "../../../../utils/modalStyles";
import { DataTable } from "../../../../components/dataTable";
import ExportExcelButton from "../ExportExcelButton";
import { pluralLabel } from "../reportPlural";
import { DAY_METHOD_COLUMN_WIDTH, useWaiterDayColumns } from "./waitersColumns";
import { exportWaiterDays } from "./waitersExport";
import type { MethodEntry } from "../../../../utils/paymentMethodsDisplay";
import type { WaiterDayRow } from "./waitersMath";

/**
 * Дата + Сметки + Средна + Оборот (140 + 88 + 128 + 136) и отстъпите на
 * модала. На таблет „Средна" е скрита (`hideBelow: "tablet"`).
 */
const DESKTOP_FIXED_WIDTH = 492 + 72;
const TABLET_FIXED_WIDTH = 364 + 72;

type Props = {
  open: boolean;
  waiterName: string;
  days: WaiterDayRow[];
  /** Същите методи като в таблицата отвън — в същия ред. */
  methodEntries: MethodEntry[];
  onClose: () => void;
};

export const WaiterDaysModal = ({
  open,
  waiterName,
  days,
  methodEntries,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();
  // Клас „Document" от рецептата за модали (880 / 720 на таблет), но расте с
  // броя методи, защото таблицата трябва да се види цялата без скрол
  // настрани. Над екрана го спира `modalWrapStyle`.
  const methodsWidth = methodEntries.length * DAY_METHOD_COLUMN_WIDTH;
  const width = isMobile
    ? undefined
    : isTablet
      ? Math.max(720, TABLET_FIXED_WIDTH + methodsWidth)
      : Math.max(880, DESKTOP_FIXED_WIDTH + methodsWidth);

  const columns = useWaiterDayColumns(methodEntries);
  const [visibleDays, setVisibleDays] = useState<WaiterDayRow[]>(days);
  // Стабилна референция — иначе ефектът в таблицата се пуска на всеки рендер.
  const handleFilteredRows = useCallback(
    (rows: WaiterDayRow[]) => setVisibleDays(rows),
    [],
  );

  const title = (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
        {t("reportsPage.waitersDaysKicker")}
      </span>
      <span className="text-[16px] font-extrabold leading-none text-ink">
        {pluralLabel(
          t,
          visibleDays.length,
          "reportsPage.waitersDayOne",
          "reportsPage.waitersDayMany",
        )}
        <span className="font-medium text-muted">
          {` · ${t("reportsPage.waitersDaysHint")}`}
        </span>
      </span>
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      <div className="bg-surface text-ink">
        <div
          className={`flex items-start gap-3 border-b border-shade-2 ${
            isNarrow ? "px-4 py-3.5" : "px-6 py-5"
          }`}
        >
          <div className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-brand-tint">
            <CalendarOutlined className="text-[20px] text-brand-deep-ink" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 truncate text-[18px] font-bold leading-[1.2] tracking-[-0.3px] text-ink">
              {waiterName}
            </h2>
            <p className="m-0 mt-1 text-[13px] font-medium leading-[1.4] text-body">
              {t("reportsPage.waitersDetailSubtitle")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="flex size-[30px] shrink-0 items-center justify-center rounded-md border-none bg-transparent text-muted transition-colors hover:bg-shade-1 hover:text-ink"
          >
            <CloseOutlined className="text-[14px]" />
          </button>
        </div>

        <div
          className={`max-h-[70vh] overflow-y-auto overscroll-contain bg-canvas ${
            isNarrow ? "p-3" : "p-4"
          }`}
        >
          <DataTable<WaiterDayRow>
            tableId="waiters-days"
            data={days}
            columns={columns}
            getRowId={(row) => row.rowKey}
            ariaLabel={t("reportsPage.waitersDetailTitle")}
            title={title}
            initialSorting={[{ id: "date", desc: true }]}
            onFilteredRowsChange={handleFilteredRows}
            // Най-дългият смислен период е месец — 50 реда го побират на една
            // страница и пагинацията не се показва без нужда.
            pageSize={50}
            toolbarRight={
              <ExportExcelButton
                onExport={() =>
                  exportWaiterDays({
                    rows: visibleDays,
                    methodEntries,
                    waiterName,
                    t,
                  })
                }
                disabled={visibleDays.length === 0}
                bare
              />
            }
            emptyTitle={t("reportsPage.waitersNoData")}
          />
        </div>
      </div>
    </Modal>
  );
};
